import React, {Component} from 'react';

import {Button} from 'primereact/button';
import {
    InputText,
    InputTextProps
} from 'primereact/inputtext';
import {useHookFormDelay} from '~/shared/src/utilities/modules/form';


const defaultProps = {

    /**
     * The search value.
     */
    value: '' as string,

    /**
     * The value to determine dirty against.
     */
    originalValue: '' as string,

    /**
     * Should the field marked if dirty.
     */
    markDirty: false as boolean,

    /**
     * The label for the field.
     */
    label: '' as string,

    /**
     * The CSS class name(s) to add to the component.
     */
    className: '' as string,

    inputStyle: { width: '100%' } as React.CSSProperties,

    /**
     * true to use react-hook-form with debounced onChange event
     */
    useHookForm: false as boolean
};

type DefaultProps = Readonly<typeof defaultProps>;

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * Callback to invoke if value has changed.
     *
     * @param {String} searchString
     */
    onChange : ( searchString : string ) => void;

    /**
     * Callback to invoke on clear button click.
     */
    onClear : () => void;

    onFocus? : () => void;
    onBlur? : () => void;

    /**
     * Callback to invoke on search button click or enter key press.
     */
    onSearch : () => void;

    style? : React.CSSProperties;
};

export type ComponentProps = Omit<InputTextProps, 'onChange'> & Props & Partial<DefaultProps>;
type AllProps = Omit<InputTextProps, 'onChange'> & Props & DefaultProps;


type State = {
    elementValue : string;
    lastValue : string;
    focused : boolean;
    mobile : boolean;
}


const getMobile = () => matchMedia( 'screen and (max-width: 960px)' ).matches ? true : false;


/**
 * search field
 */
class SearchField extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    timeoutId : NodeJS.Timeout | undefined = undefined;

    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            elementValue: props.value,
            lastValue   : props.value,
            focused     : false,
            mobile      : getMobile()
        };

        this.onMatchMobileChangeHandler = this.onMatchMobileChangeHandler.bind( this );
        this.onChange = this.onChange.bind( this );
        this.onHookFormChange = this.onHookFormChange.bind( this );
        this.onKeyDown = this.onKeyDown.bind( this );
        this.onFocus = this.onFocus.bind( this );
        this.onBlur = this.onBlur.bind( this );
    }

    /**
     * REACT lifecycle method
     */
    static getDerivedStateFromProps( props : Readonly<AllProps>, state : Readonly<State> ) {
        const retVal : Partial<State> = {};

        if ( props.value !== state.lastValue && ( !state.focused || !props.useHookForm ) ) {
            retVal.elementValue = props.value;
            retVal.lastValue = props.value;
        }

        return retVal;
    }

    componentDidMount() {
        matchMedia( 'screen and (max-width: 960px)' ).addEventListener( 'change', this.onMatchMobileChangeHandler );
    }

    componentWillUnmount() {
        matchMedia( 'screen and (max-width: 960px)' ).removeEventListener( 'change', this.onMatchMobileChangeHandler );
    }

    /**
     * Handler for InputText change event
     *
     * @private
     */
    onChange( event : React.ChangeEvent<HTMLInputElement> ) {
        let value : string = '';

        if ( event?.target?.value ) {
            value = event.target.value;
        }

        this.props.onChange( value );
    }

    /**
     * Handler for InputText change event
     *
     * @private
     */
    onHookFormChange( event : React.ChangeEvent<HTMLInputElement> ) {
        const changedValue = event.target.value;

        if ( changedValue !== this.state.elementValue ) {
            this.setState( { elementValue: changedValue } );

            if ( this.timeoutId ) {
                clearTimeout( this.timeoutId );
            }
            this.timeoutId = setTimeout( this.props.onChange.bind( null, changedValue, event ), useHookFormDelay );
        }
    }

    onMatchMobileChangeHandler() {
        this.setState( { mobile: getMobile() } );
    }

    onKeyDown( event : React.KeyboardEvent<HTMLInputElement> ) {
        const {
                  onClear,
                  onSearch
              } = this.props;

        if ( event.key === 'Enter' ) {
            onSearch();
        }
        else if ( event.key === 'Escape' ) {
            onClear();
        }
    }

    /**
     * Handler for InputText focus event
     *
     * @private
     */
    onFocus() {
        this.setState( { focused: true } );
        this.props.onFocus?.();
    }

    /**
     * Handler for InputText blur event
     *
     * @private
     */
    onBlur() {
        this.setState( { focused: false } );
        this.props.onBlur?.();
    }

    /**
     * REACT lifecycle method
     */
    render() {
        const {
                  uid,
                  value,
                  originalValue,
                  markDirty,
                  label,
                  className,
                  style,
                  inputStyle,
                  useHookForm,
                  onSearch,
                  onClear,
                  // onChange is listed here to be excluded from inputTextProps
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  onChange,
                  placeholder,
                  ...inputTextProps
              }                    = this.props,
              {
                  elementValue,
                  focused,
                  mobile
              }                    = this.state,
              inputGroupClassNames = [ 'p-inputgroup', 'yn-inputgroup' ];

        if ( focused ) {
            inputGroupClassNames.push( 'p-focus' );
        }
        if ( !label ) {
            inputGroupClassNames.push( className );
        }
        if ( markDirty && value !== originalValue ) {
            inputGroupClassNames.push( 'yn-dirty-border' );
        }

        return <div className={className + ' field'} style={style}>
            {label && !mobile &&
                <label
                    htmlFor={`${uid}_SearchField_i`}
                    className="block"
                    style={{ whiteSpace: 'nowrap' }}
                >
                    {label}
                </label>
            }
            <div
                id={`${uid}_SearchField`}
                className={inputGroupClassNames.join( ' ' )}
                style={inputStyle}
            >
                <InputText
                    {...inputTextProps as InputTextProps}
                    id={`${uid}_SearchField_i`}
                    value={elementValue}
                    pt={{
                        root: {
                            onChange : useHookForm ? this.onHookFormChange : this.onChange,
                            onKeyDown: this.onKeyDown,
                            onFocus  : this.onFocus,
                            onBlur   : this.onBlur
                        }
                    }}
                    placeholder={placeholder ? placeholder : mobile && label ? label : undefined}
                />
                {value &&
                    <Button
                        icon="pi pi-times"
                        onClick={onClear}
                    />
                }
                <Button
                    icon="pi pi-search"
                    onClick={onSearch}
                />
            </div>
        </div>;
    }
}

export default SearchField;
